<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { getCollection } from '../../../core/firebase/firestore.js'
import { getFirebaseDb } from '../../../core/firebase/firebase.js'
import { useCloudFunctions } from '@core/composables/useCloudFunctions.js'
import { collection, doc, getDocs, updateDoc, orderBy, query } from 'firebase/firestore'
import { formatDateTime } from '@core/utils/formatting.js'
import { getClientStatusClass } from '@core/utils/statusHelpers.js'
import { usePagination } from '@core/composables/usePagination.js'
import { refreshCatalogImage } from '@core/services/catalog.js'
import { useToast } from '@core/composables/useToast.js'
import { useCatalogStore } from '@app/store/catalog.js'
import CatalogImageBgModal from '@ui/components/admin/CatalogImageBgModal.vue'

const toast = useToast()
const catalogStore = useCatalogStore()

// ============================================
// State
// ============================================
const items = ref([])
const isLoading = ref(true)
const error = ref(null)

// Departments data
const departments = ref([])
const subDepartmentsMap = ref({}) // departmentId -> array of subs

// Filters
const searchQuery = ref('')
const statusFilter = ref('')
const departmentFilter = ref('')
const onlyBoxFilter = ref('') // '', 'boxes' (מארזים בלבד), 'units' (נמכר ביחידות)
const kosherRabanutFilter = ref('') // '', 'yes', 'no'
const expDaysFilter = ref('') // number of days threshold for exp_date filter

// Expand/collapse state (Set of makat strings)
const expandedRows = ref(new Set())

// Saving state per item
const savingItems = ref(new Set())

// Image refresh state per item (set of makat strings currently refreshing)
const refreshingMakats = ref(new Set())
function isRefreshingImage(item) {
  return refreshingMakats.value.has(item.makat || item.id)
}

// Stock state
const { callFunction } = useCloudFunctions()
const stockMap = ref({})
const stockLoading = ref(false)
const stockLoaded = ref(false)

// Stock filters
const stockStatusFilter = ref('') // '', 'in-stock', 'out-of-stock', 'low-stock', 'negative'
const stockWarehouseFilter = ref([]) // array of selected warehouse names
const stockMinBalance = ref('') // number or empty string
const stockMaxBalance = ref('') // number or empty string
const stockFiltersCollapsed = ref(false) // collapse state for stock filters bar
const warehouseDropdownOpen = ref(false) // warehouse dropdown open state

// ============================================
// Fetch Data
// ============================================
async function fetchCatalog() {
  error.value = null

  try {
    const docs = await getCollection('Catalog')
    items.value = docs
  } catch (err) {
    console.error('Failed to fetch catalog:', err)
    error.value = 'שגיאה בטעינת הקטלוג'
  }
}

async function fetchDepartments() {
  try {
    const db = getFirebaseDb()
    const deptRef = collection(db, 'Departments')
    const q = query(deptRef, orderBy('order', 'asc'))
    const snapshot = await getDocs(q)

    const depts = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))

    // Fetch all subcollections in parallel
    const subsEntries = await Promise.all(
      depts.map(async (dept) => {
        const subsRef = collection(db, 'Departments', dept.id, 'Subs')
        const subsQuery = query(subsRef, orderBy('order', 'asc'))
        const subsSnapshot = await getDocs(subsQuery)
        return [dept.id, subsSnapshot.docs.map((s) => ({ id: s.id, ...s.data() }))]
      })
    )

    const subsMap = Object.fromEntries(subsEntries)

    departments.value = depts
    subDepartmentsMap.value = subsMap
  } catch (err) {
    console.error('Failed to fetch departments:', err)
  }
}

onMounted(async () => {
  isLoading.value = true
  await Promise.all([fetchCatalog(), fetchDepartments()])
  isLoading.value = false
  // Add click listener for closing warehouse dropdown
  document.addEventListener('click', closeWarehouseDropdown)
})

onBeforeUnmount(() => {
  // Remove click listener
  document.removeEventListener('click', closeWarehouseDropdown)
})

// ============================================
// Department Lookups
// ============================================
const departmentLookup = computed(() => {
  const map = {}
  departments.value.forEach((d) => {
    map[d.id] = d.name
  })
  return map
})

const subDepartmentLookup = computed(() => {
  const map = {}
  Object.entries(subDepartmentsMap.value).forEach(([deptId, subs]) => {
    subs.forEach((s) => {
      map[s.id] = s.name
    })
  })
  return map
})

function getDepartmentName(id) {
  return departmentLookup.value[id] || id || '—'
}

function getSubDepartmentName(id) {
  return subDepartmentLookup.value[id] || id || '—'
}

function getSubsForDepartment(departmentId) {
  return subDepartmentsMap.value[departmentId] || []
}

// ============================================
// Update Catalog Item
// ============================================
async function updateItemDepartment(item, newDepartmentId) {
  const makat = item.makat || item.id
  savingItems.value.add(makat)

  try {
    const db = getFirebaseDb()
    await updateDoc(doc(db, 'Catalog', makat), {
      department: newDepartmentId,
      subDepartment: null, // Reset sub when department changes
    })

    // Update local state
    const idx = items.value.findIndex((i) => (i.makat || i.id) === makat)
    if (idx !== -1) {
      items.value[idx] = {
        ...items.value[idx],
        department: newDepartmentId,
        subDepartment: null,
      }
    }
  } catch (err) {
    console.error('Failed to update department:', err)
    alert('שגיאה בעדכון מחלקה')
  } finally {
    savingItems.value.delete(makat)
  }
}

async function updateItemSubDepartment(item, newSubDepartmentId) {
  const makat = item.makat || item.id
  savingItems.value.add(makat)

  try {
    const db = getFirebaseDb()
    await updateDoc(doc(db, 'Catalog', makat), {
      subDepartment: newSubDepartmentId,
    })

    // Update local state
    const idx = items.value.findIndex((i) => (i.makat || i.id) === makat)
    if (idx !== -1) {
      items.value[idx] = {
        ...items.value[idx],
        subDepartment: newSubDepartmentId,
      }
    }
  } catch (err) {
    console.error('Failed to update sub-department:', err)
    alert('שגיאה בעדכון תת-מחלקה')
  } finally {
    savingItems.value.delete(makat)
  }
}

function isItemSaving(item) {
  return savingItems.value.has(item.makat || item.id)
}

async function updateOnlyBox(item, newSpec8) {
  const makat = item.makat || item.id
  savingItems.value.add(makat)
  try {
    await callFunction('priorityUpdateItemSpec8', { makat, spec8: newSpec8 })
    const idx = items.value.findIndex((i) => (i.makat || i.id) === makat)
    if (idx !== -1) {
      items.value[idx] = { ...items.value[idx], onlyBox: newSpec8 }
    }
  } catch (err) {
    console.error('Failed to update onlyBox:', err)
    alert('שגיאה בעדכון סוג מכירה')
  } finally {
    savingItems.value.delete(makat)
  }
}

async function updateKosherRabanut(item, newSpec12) {
  const makat = item.makat || item.id
  savingItems.value.add(makat)
  try {
    await callFunction('priorityUpdateItemSpec12', { makat, spec12: newSpec12 })
    const idx = items.value.findIndex((i) => (i.makat || i.id) === makat)
    if (idx !== -1) {
      items.value[idx] = { ...items.value[idx], kosher_rabanut: newSpec12 }
    }
  } catch (err) {
    console.error('Failed to update kosher_rabanut:', err)
    alert('שגיאה בעדכון אישור רבנות')
  } finally {
    savingItems.value.delete(makat)
  }
}

function normalizeSpec(val) {
  return val === 1 || val === '1' ? '1' : '0'
}

// ============================================
// Refresh Item Image (per-row, overwrites)
// ============================================
async function handleRefreshImage(item) {
  const makat = item.makat || item.id
  if (!makat || refreshingMakats.value.has(makat)) return

  const confirmed = window.confirm(
    `לעדכן את התמונה של פריט ${makat}? פעולה זו תדרוס את התמונה הקיימת.`
  )
  if (!confirmed) return

  refreshingMakats.value = new Set(refreshingMakats.value).add(makat)

  try {
    const result = await refreshCatalogImage(makat)
    const cacheBustedUrl = `${result.imageUrl}?t=${Date.now()}`

    // Update local table state
    const idx = items.value.findIndex((i) => (i.makat || i.id) === makat)
    if (idx !== -1) {
      items.value[idx] = { ...items.value[idx], image: cacheBustedUrl }
    }

    // Update global catalog store so other views see the new image
    catalogStore.updateItemImage(makat, cacheBustedUrl)

    toast.success('התמונה עודכנה בהצלחה')
  } catch (err) {
    console.error('Failed to refresh image:', err)
    const message = err?.details?.message || err?.message || 'עדכון התמונה נכשל'
    toast.error(message)
  } finally {
    const next = new Set(refreshingMakats.value)
    next.delete(makat)
    refreshingMakats.value = next
  }
}

// ============================================
// Image Background Removal (fullscreen modal)
// ============================================
const bgRemovalItem = ref(null)

function openBgRemoval(item) {
  if (!item?.image) return
  bgRemovalItem.value = item
}

function closeBgRemoval() {
  bgRemovalItem.value = null
}

function handleImageReplaced({ makat, imageUrl }) {
  // Update local table state
  const idx = items.value.findIndex((i) => (i.makat || i.id) === makat)
  if (idx !== -1) {
    items.value[idx] = { ...items.value[idx], image: imageUrl }
  }
  // Update global catalog store so other views see the new image
  catalogStore.updateItemImage(makat, imageUrl)
}

// ============================================
// Stock Filter Computed Properties
// ============================================
const availableWarehouses = computed(() => {
  const warehouses = new Set()
  Object.values(stockMap.value).forEach((stock) => {
    stock.balances.forEach((balance) => {
      warehouses.add(balance.warehouse)
    })
  })
  return Array.from(warehouses).sort()
})

const activeStockFiltersCount = computed(() => {
  let count = 0
  if (stockStatusFilter.value) count++
  if (stockWarehouseFilter.value.length > 0) count++
  if (stockMinBalance.value !== '') count++
  if (stockMaxBalance.value !== '') count++
  return count
})

function clearStockFilters() {
  stockStatusFilter.value = ''
  stockWarehouseFilter.value = []
  stockMinBalance.value = ''
  stockMaxBalance.value = ''
  warehouseDropdownOpen.value = false
  resetPagination()
}

function removeWarehouseChip(warehouse) {
  stockWarehouseFilter.value = stockWarehouseFilter.value.filter((w) => w !== warehouse)
  resetPagination()
}

function clearSearch() {
  searchQuery.value = ''
  resetPagination()
}

function toggleWarehouseDropdown() {
  warehouseDropdownOpen.value = !warehouseDropdownOpen.value
}

function closeWarehouseDropdown(event) {
  // Close dropdown when clicking outside
  if (!event.target.closest('.warehouse-multiselect')) {
    warehouseDropdownOpen.value = false
  }
}

// ============================================
// Exp Date Helper
// ============================================
const MS_PER_DAY = 86400000

function parseDDMMYYYY(str) {
  if (!str || typeof str !== 'string') return null
  const parts = str.split('.')
  if (parts.length !== 3) return null
  const [dd, mm, yyyy] = parts.map(Number)
  if (!dd || !mm || !yyyy) return null
  return new Date(yyyy, mm - 1, dd)
}

// ============================================
// Filtering (client-side)
// ============================================
const filteredItems = computed(() => {
  let result = items.value

  // Search filter (name, makat, barcode)
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.trim().toLowerCase()
    result = result.filter((item) => {
      const name = (item.name || '').toLowerCase()
      const makat = (item.makat || '').toLowerCase()
      const barcode = (item.barcode || '').toLowerCase()
      return name.includes(query) || makat.includes(query) || barcode.includes(query)
    })
  }

  // Status filter
  if (statusFilter.value) {
    result = result.filter((item) => item.status === statusFilter.value)
  }

  // onlyBox filter
  if (onlyBoxFilter.value === 'boxes') {
    // onlyBox == 0, '0', null, undefined, false → must order in box multiples
    result = result.filter(
      (item) =>
        item.onlyBox == null || item.onlyBox === 0 || item.onlyBox === '0' || item.onlyBox === false
    )
  } else if (onlyBoxFilter.value === 'units') {
    // onlyBox == 1 or '1' → can order any quantity
    result = result.filter((item) => item.onlyBox === 1 || item.onlyBox === '1')
  }

  // kosher_rabanut filter
  if (kosherRabanutFilter.value === 'yes') {
    result = result.filter((item) => [1, '1', 2, '2'].includes(item.kosher_rabanut))
  } else if (kosherRabanutFilter.value === 'no') {
    result = result.filter(
      (item) =>
        item.kosher_rabanut == null ||
        item.kosher_rabanut === 0 ||
        item.kosher_rabanut === '0' ||
        item.kosher_rabanut === false
    )
  }

  // Exp date filter (days until expiry)
  if (expDaysFilter.value !== '') {
    const threshold = Number(expDaysFilter.value)
    if (!isNaN(threshold)) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      result = result.filter((item) => {
        const expDate = parseDDMMYYYY(item.exp_date)
        if (!expDate) return false
        const daysRemaining = (expDate - today) / MS_PER_DAY
        return daysRemaining <= threshold
      })
    }
  }

  // Stock filters (only apply if stock is loaded)
  if (stockLoaded.value) {
    // Warehouse filter (OR logic - item has stock > 0 in ANY selected warehouse)
    // Apply this first so other filters use the filtered balance
    if (stockWarehouseFilter.value.length > 0) {
      result = result.filter((item) => {
        const stock = getItemStock(item.makat)
        if (!stock) return false
        // Only include items with balance > 0 in at least one selected warehouse
        return stock.balances.some(
          (b) => stockWarehouseFilter.value.includes(b.warehouse) && b.balance > 0
        )
      })
    }

    // Stock status filter (uses filtered balance based on selected warehouses)
    if (stockStatusFilter.value) {
      result = result.filter((item) => {
        const balance = getFilteredBalance(item.makat)
        switch (stockStatusFilter.value) {
          case 'in-stock':
            return balance > 0
          case 'out-of-stock':
            return balance === 0
          case 'low-stock':
            return balance > 0 && balance < 10
          case 'negative':
            return balance < 0
          default:
            return true
        }
      })
    }

    // Min balance filter (uses filtered balance based on selected warehouses)
    if (stockMinBalance.value !== '') {
      const minVal = Number(stockMinBalance.value)
      result = result.filter((item) => getFilteredBalance(item.makat) >= minVal)
    }

    // Max balance filter (uses filtered balance based on selected warehouses)
    if (stockMaxBalance.value !== '') {
      const maxVal = Number(stockMaxBalance.value)
      result = result.filter((item) => getFilteredBalance(item.makat) <= maxVal)
    }
  }

  return result
})

// ============================================
// Pagination
// ============================================
const {
  currentPage,
  pageSize,
  totalPages,
  paginatedItems: pagedItems,
  paginationInfo: paginationInfoData,
  visiblePages,
  pageSizeOptions,
  changePageSize,
  resetPagination,
} = usePagination(filteredItems, {
  defaultPageSize: 10,
  pageSizeOptions: [10, 20, 50, 100],
})

// Custom paginationInfo text wrapper for template compatibility
const paginationInfo = computed(() => {
  const { start, end, total } = paginationInfoData.value
  if (total === 0) return 'אין פריטים'
  return `${start}-${end} מתוך ${total}`
})

// ============================================
// Unique statuses for filter dropdown
// ============================================
const uniqueStatuses = computed(() => {
  const statuses = new Set()
  items.value.forEach((item) => {
    if (item.status) statuses.add(item.status)
  })
  return Array.from(statuses).sort()
})

// ============================================
// Expand/Collapse
// ============================================
function toggleRow(makat) {
  if (expandedRows.value.has(makat)) {
    expandedRows.value.delete(makat)
  } else {
    expandedRows.value.add(makat)
  }
  // Force reactivity
  expandedRows.value = new Set(expandedRows.value)
}

function isExpanded(makat) {
  return expandedRows.value.has(makat)
}

// ============================================
// Formatting Helpers
// ============================================
function formatValue(val) {
  if (val === null || val === undefined || val === '') return '—'
  return val
}

function formatShowOnWeb(val) {
  return val === 'Y' ? 'מוצג' : 'מוסתר'
}

function getShowOnWebClass(val) {
  return val === 'Y' ? 'badge-active' : 'badge-hidden'
}

// ============================================
// Stock Functions
// ============================================
async function fetchAllStock() {
  stockLoading.value = true
  // Clear previous stock filters
  clearStockFilters()
  try {
    const result = await callFunction('priorityGetAllItemsStock')
    const map = {}
    if (result && result.items) {
      for (const item of result.items) {
        const mainWh = item.balances.find((b) => b.warehouse === 'Main')
        map[item.makat] = {
          main: mainWh ? mainWh.balance : 0,
          balances: item.balances,
        }
      }
    }
    stockMap.value = map
  } catch (err) {
    console.error('Failed to fetch stock:', err)
    alert('שגיאה בטעינת מלאים')
  } finally {
    stockLoading.value = false
    stockLoaded.value = true
  }
}

function getItemStock(makat) {
  return stockMap.value[makat] || null
}

function getMainBalance(makat) {
  const stock = stockMap.value[makat]
  return stock ? stock.main : 0
}

function getFilteredBalance(makat) {
  // Get balance based on selected warehouses, or main balance if no warehouses selected
  const stock = stockMap.value[makat]
  if (!stock) return 0

  // If no warehouses selected, use main balance
  if (stockWarehouseFilter.value.length === 0) {
    return stock.main
  }

  // Sum up stock from selected warehouses
  return stock.balances
    .filter((b) => stockWarehouseFilter.value.includes(b.warehouse))
    .reduce((sum, b) => sum + b.balance, 0)
}

function balanceClass(balance) {
  if (balance > 0) return 'balance-positive'
  if (balance < 0) return 'balance-negative'
  return 'balance-zero'
}
</script>

<template>
  <div class="admin-catalog">
    <!-- Page Header -->
    <header class="page-header">
      <div class="page-header-info">
        <div class="header-badge">
          <svg
            class="header-icon"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
          <span>ניהול מלאי</span>
        </div>
        <h1 class="page-title">קטלוג מוצרים</h1>
      </div>
      <button class="action-btn" :disabled="stockLoading" @click="fetchAllStock">
        <span v-if="stockLoading" class="action-btn-spinner"></span>
        <svg
          v-else
          class="action-btn-icon"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M3 3h18v4H3zM4 7v10a2 2 0 002 2h12a2 2 0 002-2V7" />
          <path d="M10 12h4" />
        </svg>
        טען מלאים
      </button>
    </header>

    <!-- Stock Filters Bar -->
    <Transition name="stock-filter-slide">
      <div
        v-if="stockLoaded"
        class="stock-filters-bar"
        :class="{ collapsed: stockFiltersCollapsed }"
      >
        <!-- Collapse/Expand Button -->
        <button
          class="stock-filters-toggle"
          @click="stockFiltersCollapsed = !stockFiltersCollapsed"
          :title="stockFiltersCollapsed ? 'הרחב סינוני מלאי' : 'צמצם סינוני מלאי'"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            :class="{ rotated: stockFiltersCollapsed }"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        <!-- Left: Filter controls -->
        <div v-if="!stockFiltersCollapsed" class="stock-filters-controls">
          <!-- Stock Status Dropdown -->
          <div class="stock-filter-item">
            <label class="stock-filter-label">סטטוס מלאי</label>
            <div class="select-wrapper">
              <select
                v-model="stockStatusFilter"
                class="stock-filter-select"
                @change="resetPagination"
              >
                <option value="">הכל</option>
                <option value="in-stock">במלאי</option>
                <option value="out-of-stock">אזל מהמלאי</option>
                <option value="low-stock">מלאי נמוך (&lt;10)</option>
                <option value="negative">יתרה שלילית</option>
              </select>
              <svg
                class="select-chevron"
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
          </div>

          <!-- Warehouse Filter (Multi-select) -->
          <div class="stock-filter-item stock-warehouse-filter">
            <label class="stock-filter-label">מחסנים</label>
            <div class="warehouse-multiselect">
              <div
                class="warehouse-chips"
                :class="{ open: warehouseDropdownOpen }"
                @click="toggleWarehouseDropdown"
              >
                <span v-if="stockWarehouseFilter.length === 0" class="warehouse-placeholder">
                  כל המחסנים
                </span>
                <div v-else class="warehouse-chip-list">
                  <span v-for="wh in stockWarehouseFilter" :key="wh" class="warehouse-chip">
                    {{ wh }}
                    <button class="warehouse-chip-remove" @click.stop="removeWarehouseChip(wh)">
                      ×
                    </button>
                  </span>
                </div>
                <svg
                  class="warehouse-dropdown-icon"
                  :class="{ rotated: warehouseDropdownOpen }"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
              <Transition name="dropdown-slide">
                <div v-if="warehouseDropdownOpen" class="warehouse-options">
                  <label v-for="wh in availableWarehouses" :key="wh" class="warehouse-option">
                    <input
                      type="checkbox"
                      :value="wh"
                      v-model="stockWarehouseFilter"
                      @change="resetPagination"
                    />
                    <span class="warehouse-option-label">{{ wh }}</span>
                  </label>
                </div>
              </Transition>
            </div>
          </div>

          <!-- Range Inputs -->
          <div class="stock-filter-item stock-range-group">
            <label class="stock-filter-label">טווח יתרה</label>
            <div class="stock-range-inputs">
              <input
                v-model="stockMinBalance"
                type="number"
                class="stock-range-input"
                placeholder="מינימום"
                @input="resetPagination"
              />
              <span class="stock-range-separator">—</span>
              <input
                v-model="stockMaxBalance"
                type="number"
                class="stock-range-input"
                placeholder="מקסימום"
                @input="resetPagination"
              />
            </div>
          </div>

          <div v-if="activeStockFiltersCount > 0" class="active-filters-badge">
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"
              />
            </svg>
            <span class="active-filters-count">{{ activeStockFiltersCount }}</span>
            <button class="clear-filters-btn" @click="clearStockFilters" title="נקה סינונים">
              ×
            </button>
          </div>
        </div>

        <!-- Collapsed State Indicator -->
        <div v-if="stockFiltersCollapsed" class="stock-filters-collapsed-info">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M3 3h18v4H3zM4 7v10a2 2 0 002 2h12a2 2 0 002-2V7" />
            <path d="M10 12h4" />
          </svg>
          <span class="collapsed-label">סינוני מלאי</span>
          <span v-if="activeStockFiltersCount > 0" class="collapsed-badge">
            {{ activeStockFiltersCount }}
          </span>
        </div>
      </div>
    </Transition>

    <!-- Filters Bar -->
    <div class="filters-bar">
      <div class="search-input-container">
        <svg
          class="search-icon"
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="חיפוש..."
          @input="resetPagination"
        />
        <button v-if="searchQuery" class="search-clear" @click="clearSearch">
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="filter-controls">
        <div class="select-wrapper">
          <select v-model="statusFilter" class="filter-select" @change="resetPagination">
            <option value="">כל הסטטוסים</option>
            <option v-for="status in uniqueStatuses" :key="status" :value="status">
              {{ status }}
            </option>
          </select>
          <svg
            class="select-chevron"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>

        <div class="select-wrapper">
          <select v-model="onlyBoxFilter" class="filter-select" @change="resetPagination">
            <option value="">סוג מכירה</option>
            <option value="boxes">מארזים בלבד</option>
            <option value="units">נמכר ביחידות</option>
          </select>
          <svg
            class="select-chevron"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>

        <div class="select-wrapper">
          <select v-model="kosherRabanutFilter" class="filter-select" @change="resetPagination">
            <option value="">אישור רבנות</option>
            <option value="yes">כן</option>
            <option value="no">לא</option>
          </select>
          <svg
            class="select-chevron"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>

        <div class="select-wrapper">
          <input
            v-model="expDaysFilter"
            type="number"
            min="0"
            class="filter-select"
            placeholder="ימי תוקף"
            @input="resetPagination"
          />
        </div>

        <div class="results-badge">
          <span class="results-count">{{ filteredItems.length }}</span>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner">
        <svg class="spinner-ring" viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="20" fill="none" stroke-width="4" />
        </svg>
      </div>
      <p class="loading-text">טוען...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon-wrapper">
        <svg
          viewBox="0 0 24 24"
          width="32"
          height="32"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      </div>
      <p class="error-message">{{ error }}</p>
      <button class="btn-retry" @click="fetchCatalog">נסה שוב</button>
    </div>

    <!-- Content -->
    <div v-else class="content-section">
      <!-- Desktop Table -->
      <div class="table-container desktop-only">
        <table class="data-table">
          <thead>
            <tr>
              <th class="th-expand"></th>
              <th class="th-image">תמונה</th>
              <th class="th-makat">מק״ט</th>
              <th class="th-name">שם פריט</th>
              <th class="th-department">מחלקה</th>
              <th class="th-subdepartment">תת-מחלקה</th>
              <th class="th-barcode">ברקוד</th>
              <th class="th-status">סטטוס</th>
              <th class="th-showonweb">תצוגה</th>
              <th class="th-expdate">תוקף</th>
              <th v-if="stockLoaded" class="th-stock">מלאי</th>
              <th class="th-actions">פעולות</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="item in pagedItems" :key="item.makat || item.id">
              <tr class="table-row" :class="{ 'row-expanded': isExpanded(item.makat) }">
                <td class="td-expand">
                  <button
                    class="expand-btn"
                    :class="{ expanded: isExpanded(item.makat) }"
                    @click="toggleRow(item.makat)"
                  >
                    <svg
                      class="chevron-icon"
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </td>
                <td class="td-image">
                  <div
                    class="item-image-wrapper"
                    :class="{ clickable: !!item.image }"
                    :title="item.image ? 'הסרת רקע מהתמונה' : ''"
                    @click="item.image && openBgRemoval(item)"
                  >
                    <img
                      v-if="item.image"
                      :src="item.image"
                      :alt="item.name"
                      class="item-image"
                      loading="lazy"
                    />
                    <span v-if="item.image" class="image-edit-hint" aria-hidden="true">
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path
                          d="M15 4V2M15 16v-2M8 9h2M20 9h2M17.8 11.8L19 13M17.8 6.2L19 5M12.2 6.2L11 5M3 21l9-9"
                        />
                      </svg>
                    </span>
                    <div v-else class="image-placeholder">
                      <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect
                          x="4"
                          y="8"
                          width="40"
                          height="32"
                          rx="4"
                          stroke="currentColor"
                          stroke-width="2"
                        />
                        <circle cx="16" cy="20" r="4" stroke="currentColor" stroke-width="2" />
                        <path
                          d="M4 32l10-10a2 2 0 012.8 0l8.2 8.2"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                        <path
                          d="M28 28l4-4a2 2 0 012.8 0L44 33"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                </td>
                <td class="td-makat">
                  <span class="makat-value">{{ formatValue(item.makat) }}</span>
                </td>
                <td class="td-name">{{ formatValue(item.name) }}</td>
                <td class="td-department">
                  <div class="table-select-wrapper">
                    <select
                      class="table-select"
                      :value="item.department || ''"
                      :disabled="isItemSaving(item)"
                      @change="updateItemDepartment(item, $event.target.value)"
                    >
                      <option value="">—</option>
                      <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                        {{ dept.name }}
                      </option>
                    </select>
                    <span v-if="isItemSaving(item)" class="table-saving-indicator"></span>
                  </div>
                </td>
                <td class="td-subdepartment">
                  <div class="table-select-wrapper">
                    <select
                      class="table-select"
                      :value="item.subDepartment || ''"
                      :disabled="isItemSaving(item) || !item.department"
                      @change="updateItemSubDepartment(item, $event.target.value)"
                    >
                      <option value="">—</option>
                      <option
                        v-for="sub in getSubsForDepartment(item.department)"
                        :key="sub.id"
                        :value="sub.id"
                      >
                        {{ sub.name }}
                      </option>
                    </select>
                    <span v-if="isItemSaving(item)" class="table-saving-indicator"></span>
                  </div>
                </td>
                <td class="td-barcode">
                  <code class="barcode-value">{{ formatValue(item.barcode) }}</code>
                </td>
                <td class="td-status">
                  <span class="status-badge" :class="getClientStatusClass(item.status)">
                    {{ formatValue(item.status) }}
                  </span>
                </td>
                <td class="td-showonweb">
                  <span class="visibility-badge" :class="getShowOnWebClass(item.showOnWeb)">
                    {{ formatShowOnWeb(item.showOnWeb) }}
                  </span>
                </td>
                <td class="td-expdate">{{ formatValue(item.exp_date) }}</td>
                <td v-if="stockLoaded" class="td-stock">
                  <span class="stock-balance" :class="balanceClass(getMainBalance(item.makat))">
                    {{ getMainBalance(item.makat) }}
                  </span>
                </td>
                <td class="td-actions">
                  <button
                    class="btn-refresh-image"
                    :disabled="isRefreshingImage(item)"
                    :title="'עדכון תמונה מפריוריטי'"
                    @click="handleRefreshImage(item)"
                  >
                    <svg
                      v-if="!isRefreshingImage(item)"
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M23 4v6h-6" />
                      <path d="M1 20v-6h6" />
                      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10" />
                      <path d="M20.49 15A9 9 0 015.64 18.36L1 14" />
                    </svg>
                    <span v-else class="btn-spinner" aria-hidden="true"></span>
                    <span class="btn-refresh-image-label">עדכון תמונה</span>
                  </button>
                </td>
              </tr>

              <!-- Details Row -->
              <tr v-if="isExpanded(item.makat)" class="details-row">
                <td :colspan="stockLoaded ? 12 : 11">
                  <div class="details-container" style="position: relative">
                    <div v-if="isItemSaving(item)" class="saving-overlay">
                      <div class="saving-spinner"></div>
                      <span class="saving-text">מעדכן...</span>
                    </div>
                    <div class="details-grid">
                      <div class="detail-item detail-item-select">
                        <span class="detail-label">מחלקה</span>
                        <div class="detail-select-wrapper">
                          <select
                            class="detail-select"
                            :value="item.department || ''"
                            :disabled="isItemSaving(item)"
                            @change="updateItemDepartment(item, $event.target.value)"
                          >
                            <option value="">— בחר מחלקה —</option>
                            <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                              {{ dept.name }}
                            </option>
                          </select>
                          <svg
                            class="detail-select-chevron"
                            viewBox="0 0 24 24"
                            width="12"
                            height="12"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                          >
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                          <span v-if="isItemSaving(item)" class="saving-indicator"></span>
                        </div>
                      </div>
                      <div class="detail-item detail-item-select">
                        <span class="detail-label">תת-מחלקה</span>
                        <div class="detail-select-wrapper">
                          <select
                            class="detail-select"
                            :value="item.subDepartment || ''"
                            :disabled="isItemSaving(item) || !item.department"
                            @change="updateItemSubDepartment(item, $event.target.value)"
                          >
                            <option value="">— בחר תת-מחלקה —</option>
                            <option
                              v-for="sub in getSubsForDepartment(item.department)"
                              :key="sub.id"
                              :value="sub.id"
                            >
                              {{ sub.name }}
                            </option>
                          </select>
                          <svg
                            class="detail-select-chevron"
                            viewBox="0 0 24 24"
                            width="12"
                            height="12"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                          >
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                          <span v-if="isItemSaving(item)" class="saving-indicator"></span>
                        </div>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">יחידת מידה</span>
                        <span class="detail-value">{{ formatValue(item.measure) }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">מקור</span>
                        <span class="detail-value">{{ formatValue(item.origin) }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">יצרן</span>
                        <span class="detail-value">{{ formatValue(item.factory) }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">תפוגה</span>
                        <span class="detail-value">{{ formatValue(item.exp_date) }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">סוג מכירה</span>
                        <select
                          class="detail-select"
                          :value="normalizeSpec(item.onlyBox)"
                          @change="updateOnlyBox(item, $event.target.value)"
                        >
                          <option value="0">רק מארזים</option>
                          <option value="1">נמכר ביחידות</option>
                        </select>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">גודל קרטון</span>
                        <span class="detail-value">{{ formatValue(item.boxSize) }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">ברקוד קרטון</span>
                        <span class="detail-value">{{ formatValue(item.boxBarcode) }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">השגחת כשרות</span>
                        <span class="detail-value">{{ formatValue(item.kosher_type) }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">אישור רבנות</span>
                        <select
                          class="detail-select"
                          :value="normalizeSpec(item.kosher_rabanut)"
                          @change="updateKosherRabanut(item, $event.target.value)"
                        >
                          <option value="0">לא</option>
                          <option value="1">כן</option>
                        </select>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">החזרות</span>
                        <span class="detail-value">{{ formatValue(item.returns) }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">פיקדון</span>
                        <span class="detail-value">{{ formatValue(item.pikadon) }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">עדכון אחרון</span>
                        <span class="detail-value">{{
                          formatDateTime(item.last_update, { short: true })
                        }}</span>
                      </div>
                    </div>
                    <div v-if="getItemStock(item.makat)" class="stock-section">
                      <div class="stock-section-header">
                        <svg
                          viewBox="0 0 24 24"
                          width="14"
                          height="14"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path d="M3 3h18v4H3zM4 7v10a2 2 0 002 2h12a2 2 0 002-2V7" />
                          <path d="M10 12h4" />
                        </svg>
                        <span>מלאי לפי מחסנים</span>
                      </div>
                      <div class="stock-grid">
                        <div
                          v-for="wh in getItemStock(item.makat).balances"
                          :key="wh.warehouse"
                          class="stock-chip"
                        >
                          <span class="stock-wh-name">{{ wh.warehouse }}</span>
                          <span class="stock-wh-balance" :class="balanceClass(wh.balance)">{{
                            wh.balance
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="pagedItems.length === 0">
              <td :colspan="stockLoaded ? 12 : 11" class="empty-state">
                <p>לא נמצאו פריטים</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Cards -->
      <div class="cards-container mobile-only">
        <div
          v-for="item in pagedItems"
          :key="item.makat || item.id"
          class="item-card"
          :class="{ expanded: isExpanded(item.makat) }"
        >
          <div class="card-header" @click="toggleRow(item.makat)">
            <div class="card-image-wrapper">
              <img
                v-if="item.image"
                :src="item.image"
                :alt="item.name"
                class="card-image"
                loading="lazy"
                @click.stop="openBgRemoval(item)"
              />
              <div v-else class="image-placeholder">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect
                    x="4"
                    y="8"
                    width="40"
                    height="32"
                    rx="4"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                  <circle cx="16" cy="20" r="4" stroke="currentColor" stroke-width="2" />
                  <path
                    d="M4 32l10-10a2 2 0 012.8 0l8.2 8.2"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                  <path
                    d="M28 28l4-4a2 2 0 012.8 0L44 33"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </div>
              <button
                class="btn-refresh-image-card"
                :disabled="isRefreshingImage(item)"
                :title="'עדכון תמונה מפריוריטי'"
                @click.stop="handleRefreshImage(item)"
              >
                <svg
                  v-if="!isRefreshingImage(item)"
                  viewBox="0 0 24 24"
                  width="12"
                  height="12"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M23 4v6h-6" />
                  <path d="M1 20v-6h6" />
                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10" />
                  <path d="M20.49 15A9 9 0 015.64 18.36L1 14" />
                </svg>
                <span v-else class="btn-spinner" aria-hidden="true"></span>
              </button>
            </div>
            <div class="card-main">
              <span class="card-makat">{{ formatValue(item.makat) }}</span>
              <h3 class="card-name">{{ formatValue(item.name) }}</h3>
            </div>
            <div class="card-badges">
              <span class="status-badge" :class="getClientStatusClass(item.status)">
                {{ formatValue(item.status) }}
              </span>
              <span class="visibility-badge" :class="getShowOnWebClass(item.showOnWeb)">
                {{ formatShowOnWeb(item.showOnWeb) }}
              </span>
              <span
                v-if="stockLoaded"
                class="stock-badge"
                :class="balanceClass(getMainBalance(item.makat))"
              >
                {{ getMainBalance(item.makat) }}
              </span>
            </div>
            <svg
              class="card-chevron"
              :class="{ expanded: isExpanded(item.makat) }"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>

          <div v-if="isExpanded(item.makat)" class="card-details" style="position: relative">
            <div v-if="isItemSaving(item)" class="saving-overlay">
              <div class="saving-spinner"></div>
              <span class="saving-text">מעדכן...</span>
            </div>
            <div class="card-row card-row-select">
              <span class="card-label">מחלקה</span>
              <div class="card-select-wrapper">
                <select
                  class="card-select"
                  :value="item.department || ''"
                  :disabled="isItemSaving(item)"
                  @change="updateItemDepartment(item, $event.target.value)"
                >
                  <option value="">— בחר —</option>
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                    {{ dept.name }}
                  </option>
                </select>
                <span v-if="isItemSaving(item)" class="saving-indicator"></span>
              </div>
            </div>
            <div class="card-row card-row-select">
              <span class="card-label">תת-מחלקה</span>
              <div class="card-select-wrapper">
                <select
                  class="card-select"
                  :value="item.subDepartment || ''"
                  :disabled="isItemSaving(item) || !item.department"
                  @change="updateItemSubDepartment(item, $event.target.value)"
                >
                  <option value="">— בחר —</option>
                  <option
                    v-for="sub in getSubsForDepartment(item.department)"
                    :key="sub.id"
                    :value="sub.id"
                  >
                    {{ sub.name }}
                  </option>
                </select>
                <span v-if="isItemSaving(item)" class="saving-indicator"></span>
              </div>
            </div>
            <div class="card-divider"></div>
            <div class="card-row">
              <span class="card-label">ברקוד</span>
              <code class="card-value mono">{{ formatValue(item.barcode) }}</code>
            </div>
            <div class="card-row">
              <span class="card-label">עדכון אחרון</span>
              <span class="card-value">{{
                formatDateTime(item.last_update, { short: true })
              }}</span>
            </div>
            <div class="card-divider"></div>
            <div class="card-grid">
              <div class="card-grid-item">
                <span class="card-label">יחידת מידה</span>
                <span class="card-value">{{ formatValue(item.measure) }}</span>
              </div>
              <div class="card-grid-item">
                <span class="card-label">מקור</span>
                <span class="card-value">{{ formatValue(item.origin) }}</span>
              </div>
              <div class="card-grid-item">
                <span class="card-label">יצרן</span>
                <span class="card-value">{{ formatValue(item.factory) }}</span>
              </div>
              <div class="card-grid-item">
                <span class="card-label">תפוגה</span>
                <span class="card-value">{{ formatValue(item.exp_date) }}</span>
              </div>
              <div class="card-grid-item">
                <span class="card-label">גודל קרטון</span>
                <span class="card-value">{{ formatValue(item.boxSize) }}</span>
              </div>
              <div class="card-grid-item">
                <span class="card-label">השגחת כשרות</span>
                <span class="card-value">{{ formatValue(item.kosher_type) }}</span>
              </div>
              <div class="card-grid-item">
                <span class="card-label">אישור רבנות</span>
                <select
                  class="detail-select"
                  :value="normalizeSpec(item.kosher_rabanut)"
                  @change="updateKosherRabanut(item, $event.target.value)"
                >
                  <option value="0">לא</option>
                  <option value="1">כן</option>
                </select>
              </div>
              <div class="card-grid-item">
                <span class="card-label">סוג מכירה</span>
                <select
                  class="detail-select"
                  :value="normalizeSpec(item.onlyBox)"
                  @change="updateOnlyBox(item, $event.target.value)"
                >
                  <option value="0">רק מארזים</option>
                  <option value="1">נמכר ביחידות</option>
                </select>
              </div>
            </div>
            <template v-if="getItemStock(item.makat)">
              <div class="card-divider"></div>
              <div class="card-stock-section">
                <div class="card-stock-header">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M3 3h18v4H3zM4 7v10a2 2 0 002 2h12a2 2 0 002-2V7" />
                    <path d="M10 12h4" />
                  </svg>
                  <span>מלאי לפי מחסנים</span>
                </div>
                <div class="card-stock-grid">
                  <div
                    v-for="wh in getItemStock(item.makat).balances"
                    :key="wh.warehouse"
                    class="stock-chip"
                  >
                    <span class="stock-wh-name">{{ wh.warehouse }}</span>
                    <span class="stock-wh-balance" :class="balanceClass(wh.balance)">{{
                      wh.balance
                    }}</span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div v-if="pagedItems.length === 0" class="empty-card">
          <p>לא נמצאו פריטים</p>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="!isLoading && !error && items.length > 0" class="pagination-bar">
      <div class="pagination-right">
        <span class="pagination-info">{{ paginationInfo }}</span>
        <div class="page-size-selector">
          <select
            :value="pageSize"
            class="page-size-select"
            @change="changePageSize(Number($event.target.value))"
          >
            <option v-for="size in pageSizeOptions" :key="size" :value="size">
              {{ size }}
            </option>
          </select>
          <span class="page-size-label">לעמוד</span>
        </div>
      </div>
      <div class="pagination-controls">
        <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage--">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        <div class="pagination-pages">
          <button
            v-for="page in visiblePages"
            :key="page"
            class="page-btn"
            :class="{ active: page === currentPage, ellipsis: page === '...' }"
            :disabled="page === '...'"
            @click="page !== '...' && (currentPage = page)"
          >
            {{ page }}
          </button>
        </div>

        <button
          class="pagination-btn"
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Image Background Removal Modal -->
    <CatalogImageBgModal
      :open="!!bgRemovalItem"
      :item="bgRemovalItem"
      @close="closeBgRemoval"
      @replaced="handleImageReplaced"
    />
  </div>
</template>

<style scoped>
/* ============================================
   Base Layout
   ============================================ */
.admin-catalog {
  padding: var(--spacing-md);
  max-width: 1400px;
  margin: 0 auto;
  height: calc(100vh - var(--header-height));
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.content-section {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Custom scrollbar */
.table-container::-webkit-scrollbar,
.cards-container::-webkit-scrollbar {
  width: 6px;
}

.table-container::-webkit-scrollbar-track,
.cards-container::-webkit-scrollbar-track {
  background: var(--bg-secondary);
  border-radius: 3px;
}

.table-container::-webkit-scrollbar-thumb,
.cards-container::-webkit-scrollbar-thumb {
  background: var(--color-gray-300);
  border-radius: 3px;
}

.table-container::-webkit-scrollbar-thumb:hover,
.cards-container::-webkit-scrollbar-thumb:hover {
  background: var(--color-gray-400);
}

/* ============================================
   Page Header - Compact
   ============================================ */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-md);
  flex-shrink: 0;
}

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  margin-bottom: 4px;
}

.header-icon {
  color: var(--color-primary);
}

.page-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

/* ============================================
   Filters Bar - Compact
   ============================================ */
.filters-bar {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
  margin-bottom: var(--spacing-md);
  padding: var(--spacing-sm);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-sm);
  flex-shrink: 0;
}

.search-input-container {
  position: relative;
  flex: 1;
  min-width: 0;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  padding-right: 34px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary);
  background: var(--bg-primary);
}

.search-clear {
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: var(--bg-tertiary);
  border: none;
  border-radius: var(--border-radius-full);
  color: var(--text-secondary);
  cursor: pointer;
}

.filter-controls {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.select-wrapper {
  position: relative;
}

.select-chevron {
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.filter-select {
  appearance: none;
  padding: 8px 10px 8px 24px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
  min-width: 80px;
}

.filter-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.results-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  background: var(--color-primary);
  border-radius: var(--border-radius-full);
}

.results-count {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: white;
}

/* ============================================
   Loading & Error States
   ============================================ */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2xl);
  flex: 1;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  margin-bottom: var(--spacing-sm);
}

.spinner-ring {
  animation: spin 1s linear infinite;
}

.spinner-ring circle {
  stroke: var(--color-primary);
  stroke-linecap: round;
  stroke-dasharray: 100;
  stroke-dashoffset: 40;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2xl);
  text-align: center;
  flex: 1;
}

.error-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: var(--color-error-light);
  border-radius: var(--border-radius-full);
  margin-bottom: var(--spacing-md);
  color: var(--color-error);
}

.error-message {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-md) 0;
}

.btn-retry {
  padding: 8px 16px;
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
}

/* ============================================
   Desktop Table
   ============================================ */
.table-container {
  background: var(--bg-primary);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: var(--shadow-sm);
  flex: 1;
  min-height: 0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

.data-table th {
  text-align: right;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.data-table td {
  padding: 10px 12px;
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color-light);
}

.table-row {
  transition: background var(--transition-fast);
}

.table-row:hover {
  background: var(--bg-secondary);
}

.row-expanded {
  background: var(--bg-secondary);
}

/* Column Styles */
.th-expand,
.td-expand {
  width: 40px;
  text-align: center;
}
.th-image,
.td-image {
  width: 56px;
  text-align: center;
}
.th-makat,
.td-makat {
  width: 70px;
}
.th-name,
.td-name {
  min-width: 120px;
}
.th-department,
.td-department {
  width: 130px;
}
.th-subdepartment,
.td-subdepartment {
  width: 130px;
}
.th-barcode,
.td-barcode {
  width: 100px;
}
.th-status,
.td-status {
  width: 70px;
  text-align: center;
}
.th-showonweb,
.td-showonweb {
  width: 60px;
  text-align: center;
}
.th-expdate,
.td-expdate {
  width: 80px;
  font-size: 11px;
  color: var(--text-secondary);
}

/* Table Select Inputs */
.table-select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.table-select {
  appearance: none;
  width: 100%;
  padding: 4px 6px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 11px;
  font-family: var(--font-family);
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color var(--transition-fast);
  max-width: 120px;
  text-overflow: ellipsis;
}

.table-select:hover:not(:disabled) {
  border-color: var(--color-primary-light);
}

.table-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.table-select:disabled {
  background: var(--bg-tertiary);
  cursor: not-allowed;
  opacity: 0.6;
}

.table-saving-indicator {
  position: absolute;
  left: -14px;
  top: 50%;
  transform: translateY(-50%);
  width: 10px;
  height: 10px;
  border: 2px solid var(--color-primary-light);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Item Image - Table */
.item-image-wrapper {
  width: 44px;
  height: 44px;
  border-radius: var(--border-radius-sm);
  overflow: hidden;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
}

.item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-image-wrapper.clickable {
  position: relative;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.item-image-wrapper.clickable:hover {
  border-color: var(--color-primary-light);
  box-shadow: 0 2px 8px rgba(30, 41, 59, 0.25);
}

.image-edit-hint {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
  color: #ffffff;
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;
}

.item-image-wrapper.clickable:hover .image-edit-hint {
  opacity: 1;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-gray-100) 0%, var(--color-gray-50) 100%);
  color: var(--color-gray-300);
  border-radius: inherit;
}

.image-placeholder svg {
  width: 60%;
  height: 60%;
}

.makat-value {
  font-family: var(--font-family-mono);
  font-weight: var(--font-weight-semibold);
  font-size: 12px;
  color: var(--color-primary-dark);
  background: rgba(99, 102, 241, 0.08);
  padding: 2px 6px;
  border-radius: 4px;
}

.barcode-value {
  font-family: var(--font-family-mono);
  font-size: 11px;
  color: var(--text-secondary);
}

/* Badges - Centered */
.status-badge,
.visibility-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

.status-active {
  background: var(--color-success-light);
  color: var(--color-success);
}

.status-inactive {
  background: var(--color-gray-100);
  color: var(--color-gray-500);
}

.status-default {
  background: var(--color-info-light);
  color: var(--color-info);
}

.visibility-badge.badge-active {
  background: var(--color-success-light);
  color: var(--color-success);
}

.visibility-badge.badge-hidden {
  background: var(--color-gray-100);
  color: var(--color-gray-500);
}

/* Expand Button */
.expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.expand-btn:hover {
  border-color: var(--color-primary);
}

.chevron-icon {
  transition: transform var(--transition-fast);
  color: var(--text-secondary);
}

.expand-btn.expanded {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

.expand-btn.expanded .chevron-icon {
  transform: rotate(180deg);
  color: white;
}

/* Saving Overlay */
.saving-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: inherit;
  backdrop-filter: blur(2px);
}

.saving-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--border-color, #e2e8f0);
  border-top-color: var(--color-primary, #283548);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.saving-text {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary, #64748b);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Details Row */
.details-row td {
  padding: 0 !important;
  background: var(--bg-secondary);
}

.details-container {
  padding: 12px 16px;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 10px;
  color: var(--text-muted);
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
}

.detail-value {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
}

/* Detail Select Inputs */
.detail-item-select {
  grid-column: span 1;
}

.detail-select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.detail-select {
  appearance: none;
  width: 100%;
  padding: 6px 24px 6px 8px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-family: var(--font-family);
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color var(--transition-fast);
}

.detail-select:hover:not(:disabled) {
  border-color: var(--color-primary-light);
}

.detail-select:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
}

.detail-select:disabled {
  background: var(--bg-tertiary);
  cursor: not-allowed;
  opacity: 0.6;
}

.detail-select-chevron {
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

/* Saving Indicator */
.saving-indicator {
  position: absolute;
  left: -18px;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  border: 2px solid var(--color-primary-light);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.empty-state {
  text-align: center;
  padding: var(--spacing-xl) !important;
  color: var(--text-muted);
}

/* ============================================
   Mobile Cards
   ============================================ */
.cards-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  overflow-y: auto;
  padding-left: 2px;
  padding-right: 2px;
  flex: 1;
  min-height: 0;
}

.item-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.item-card.expanded {
  border-color: var(--color-primary-light);
  box-shadow: var(--shadow-md);
}

.card-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 12px;
  cursor: pointer;
}

.card-image-wrapper {
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius-sm);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  flex-shrink: 0;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--border-radius-sm);
  cursor: pointer;
}

.card-main {
  flex: 1;
  min-width: 0;
}

.card-makat {
  font-family: var(--font-family-mono);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
  padding: 1px 6px;
  border-radius: 4px;
}

.card-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  margin: 4px 0 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-badges {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
}

.card-chevron {
  color: var(--text-muted);
  transition: transform var(--transition-fast);
  flex-shrink: 0;
}

.card-chevron.expanded {
  transform: rotate(180deg);
  color: var(--color-primary);
}

.card-details {
  padding: 0 12px 12px 12px;
  animation: slideDown 0.2s ease-out;
}

.card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid var(--border-color-light);
}

.card-label {
  font-size: 12px;
  color: var(--text-secondary);
}

.card-value {
  font-size: 12px;
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
}

.card-value.mono {
  font-family: var(--font-family-mono);
}

/* Card Select Inputs */
.card-row-select {
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
}

.card-select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.card-select {
  appearance: none;
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-family: var(--font-family);
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
}

.card-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.card-select:disabled {
  background: var(--bg-tertiary);
  cursor: not-allowed;
  opacity: 0.6;
}

.card-select-wrapper .saving-indicator {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
}

.card-divider {
  height: 1px;
  background: var(--border-color);
  margin: 8px 0;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.card-grid-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.empty-card {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-muted);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
}

/* ============================================
   Pagination - Compact
   ============================================ */
.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  flex-shrink: 0;
}

.pagination-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.pagination-info {
  font-size: 12px;
  color: var(--text-secondary);
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-size-select {
  padding: 4px 8px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-family: var(--font-family);
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
}

.page-size-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.page-size-label {
  font-size: 12px;
  color: var(--text-muted);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.pagination-btn:hover:not(:disabled) {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-pages {
  display: flex;
  gap: 2px;
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.page-btn:hover:not(:disabled):not(.active) {
  background: var(--bg-secondary);
}

.page-btn.active {
  background: var(--color-primary);
  color: white;
}

.page-btn.ellipsis {
  cursor: default;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
  box-shadow: var(--shadow-sm);
}

.action-btn:hover:not(:disabled) {
  border-color: var(--color-primary);
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.05);
}

.action-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.action-btn-icon {
  flex-shrink: 0;
}

.action-btn-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid var(--color-primary-light);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}

/* ============================================
   Stock Column (Desktop Table)
   ============================================ */
.th-stock,
.td-stock {
  width: 70px;
  text-align: center;
}

/* ============================================
   Actions Column — Refresh Image Button
   ============================================ */
.th-actions,
.td-actions {
  width: 110px;
  text-align: center;
}

.btn-refresh-image {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 8px;
  background: var(--bg-secondary);
  color: var(--color-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.btn-refresh-image:hover:not(:disabled) {
  background: var(--color-primary);
  color: #fff;
  border-color: var(--color-primary);
}
.btn-refresh-image:disabled {
  opacity: 0.6;
  cursor: wait;
}
.btn-refresh-image-label {
  line-height: 1;
}

.btn-refresh-image-card {
  position: absolute;
  inset-inline-end: 4px;
  inset-block-start: 4px;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  color: var(--color-primary);
  border: 1px solid var(--border-color);
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  z-index: 2;
}
.btn-refresh-image-card:hover:not(:disabled) {
  background: var(--color-primary);
  color: #fff;
  border-color: var(--color-primary);
}
.btn-refresh-image-card:disabled {
  opacity: 0.6;
  cursor: wait;
}

.btn-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: btn-spinner-rotate 0.7s linear infinite;
  display: inline-block;
}
@keyframes btn-spinner-rotate {
  to {
    transform: rotate(360deg);
  }
}

.stock-balance {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  padding: 2px 8px;
  border-radius: var(--border-radius-full);
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family-mono);
}

.balance-positive {
  background: var(--color-success-light);
  color: var(--color-success);
}

.balance-negative {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.balance-zero {
  background: var(--color-gray-100);
  color: var(--color-gray-500);
}

/* ============================================
   Stock Section (Desktop Details)
   ============================================ */
.stock-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
}

.stock-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}

.stock-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.stock-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-full);
  font-size: 12px;
}

.stock-wh-name {
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

.stock-wh-balance {
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-mono);
}

/* ============================================
   Stock Badge (Mobile Cards)
   ============================================ */
.stock-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-mono);
}

/* ============================================
   Stock Section (Mobile Cards)
   ============================================ */
.card-stock-section {
  padding-top: 4px;
}

.card-stock-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}

.card-stock-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* ============================================
   Responsive - Desktop (default)
   ============================================ */
.table-container.desktop-only {
  display: block;
}

.cards-container.mobile-only {
  display: none;
}

@media (min-width: 769px) {
  .admin-catalog {
    padding: var(--spacing-lg);
  }
}

/* ============================================
   Responsive - Mobile
   ============================================ */
@media (max-width: 768px) {
  .admin-catalog {
    padding: var(--spacing-sm);
    height: calc(100vh - 56px); /* Mobile header height */
  }

  .table-container.desktop-only {
    display: none;
  }

  .cards-container.mobile-only {
    display: flex;
  }

  .filters-bar {
    flex-wrap: wrap;
  }

  .search-input-container {
    flex: 1 1 100%;
    order: 1;
  }

  .filter-controls {
    order: 2;
    flex: 1 1 100%;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .filter-select {
    min-width: 0;
    font-size: var(--font-size-xs);
  }

  .pagination-bar {
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .pagination-right {
    width: 100%;
    justify-content: space-between;
  }

  .pagination-pages {
    display: none;
  }

  .pagination-controls {
    width: 100%;
    justify-content: space-between;
  }

  .pagination-btn {
    flex: 1;
    max-width: 120px;
  }
}

/* ============================================
   Stock Filters Bar
   ============================================ */
.stock-filters-bar {
  position: relative;
  display: flex;
  gap: var(--spacing-md);
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: var(--spacing-md);
  padding: var(--spacing-md);
  background: linear-gradient(135deg, #d1fae5 0%, #ecfdf5 100%);
  border: 1px solid var(--color-success);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-sm);
  flex-shrink: 0;
  transition: all var(--transition-base);
}

.stock-filters-bar.collapsed {
  padding: var(--spacing-sm) var(--spacing-md);
  background: linear-gradient(135deg, #d1fae5 0%, #d1fae5 100%);
}

.stock-filters-bar.collapsed .stock-filters-toggle {
  top: 50%;
  transform: translateY(-50%);
}

.stock-filters-toggle {
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: white;
  border: 1px solid #a7f3d0;
  border-radius: var(--border-radius-sm);
  color: #047857;
  cursor: pointer;
  transition:
    background var(--transition-fast),
    border-color var(--transition-fast);
  z-index: 1;
}

.stock-filters-toggle:hover {
  background: #f0fdf4;
  border-color: var(--color-success);
  transform: scale(1.05);
}

.stock-filters-toggle svg {
  transition: transform var(--transition-base);
}

.stock-filters-toggle svg.rotated {
  transform: rotate(180deg);
}

.stock-filters-collapsed-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.stock-filters-collapsed-info svg {
  color: #047857;
  flex-shrink: 0;
}

.collapsed-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: #047857;
}

.collapsed-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #047857;
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  color: white;
}

.stock-filters-controls {
  display: flex;
  gap: var(--spacing-md);
  align-items: flex-end;
  flex-wrap: wrap;
  flex: 1;
}

.stock-filter-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stock-filter-label {
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: #047857;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.stock-filter-select {
  appearance: none;
  padding: 8px 28px 8px 12px;
  border: 1px solid #a7f3d0;
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: white;
  color: var(--text-primary);
  cursor: pointer;
  min-width: 140px;
  transition: all var(--transition-fast);
}

.stock-filter-select:hover {
  border-color: var(--color-success);
}

.stock-filter-select:focus {
  outline: none;
  border-color: var(--color-success);
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.stock-range-group {
  min-width: 200px;
}

.stock-range-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stock-range-input {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #a7f3d0;
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: white;
  color: var(--text-primary);
  min-width: 0;
  transition: all var(--transition-fast);
}

.stock-range-input::placeholder {
  color: var(--text-muted);
  opacity: 0.6;
}

.stock-range-input:hover {
  border-color: var(--color-success);
}

.stock-range-input:focus {
  outline: none;
  border-color: var(--color-success);
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.stock-range-separator {
  color: #047857;
  font-weight: var(--font-weight-bold);
  flex-shrink: 0;
}

/* Warehouse Multi-Select */
.stock-warehouse-filter {
  min-width: 220px;
}

.warehouse-multiselect {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.warehouse-chips {
  position: relative;
  min-height: 36px;
  padding: 6px 28px 6px 10px;
  background: white;
  border: 1px solid #a7f3d0;
  border-radius: var(--border-radius-sm);
  transition: all var(--transition-fast);
  cursor: pointer;
  user-select: none;
}

.warehouse-chips:hover {
  border-color: var(--color-success);
}

.warehouse-chips.open {
  border-color: var(--color-success);
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.warehouse-dropdown-icon {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #047857;
  transition: transform var(--transition-base);
  pointer-events: none;
}

.warehouse-dropdown-icon.rotated {
  transform: translateY(-50%) rotate(180deg);
}

.warehouse-placeholder {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.warehouse-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.warehouse-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px 2px 8px;
  background: #047857;
  color: white;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: var(--font-weight-medium);
}

.warehouse-chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: var(--border-radius-full);
  color: white;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.warehouse-chip-remove:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.warehouse-options {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
  padding: 6px;
  background: white;
  border: 1px solid var(--color-success);
  border-radius: var(--border-radius-sm);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
}

.warehouse-options::-webkit-scrollbar {
  width: 4px;
}

.warehouse-options::-webkit-scrollbar-track {
  background: #f0fdf4;
}

.warehouse-options::-webkit-scrollbar-thumb {
  background: #a7f3d0;
  border-radius: 2px;
}

.warehouse-options::-webkit-scrollbar-thumb:hover {
  background: var(--color-success);
}

.warehouse-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  cursor: pointer;
  border-radius: var(--border-radius-sm);
  transition: background var(--transition-fast);
}

.warehouse-option:hover {
  background: #f0fdf4;
}

.warehouse-option input[type='checkbox'] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #047857;
}

.warehouse-option-label {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  user-select: none;
}

.active-filters-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #047857;
  border-radius: var(--border-radius-full);
  color: white;
}

.active-filters-badge svg {
  color: white;
  flex-shrink: 0;
}

.active-filters-count {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: white;
}

.clear-filters-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: var(--border-radius-full);
  color: white;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.clear-filters-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

/* Stock Filter Transition */
.stock-filter-slide-enter-active,
.stock-filter-slide-leave-active {
  transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
}

.stock-filter-slide-enter-from {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
  margin-bottom: 0;
}

.stock-filter-slide-enter-to {
  opacity: 1;
  max-height: 200px;
  transform: translateY(0);
  margin-bottom: var(--spacing-md);
}

.stock-filter-slide-leave-from {
  opacity: 1;
  max-height: 200px;
  transform: translateY(0);
  margin-bottom: var(--spacing-md);
}

.stock-filter-slide-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
  margin-bottom: 0;
}

/* Dropdown Transition */
.dropdown-slide-enter-active,
.dropdown-slide-leave-active {
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-slide-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-slide-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.dropdown-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.dropdown-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .stock-filters-bar {
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .stock-filters-controls {
    flex-direction: column;
    gap: var(--spacing-sm);
    width: 100%;
  }

  .stock-filter-item {
    width: 100%;
  }

  .stock-filter-select {
    width: 100%;
    min-width: 0;
  }

  .stock-range-group {
    width: 100%;
    min-width: 0;
  }

  .stock-warehouse-filter {
    width: 100%;
    min-width: 0;
  }

  .stock-filters-collapsed-info {
    width: 100%;
  }
}
</style>
